// ─── EmailLookup.utils.js ─────────────────────────────────────────────────────
// Pure business logic — no React, no state, no UI concerns.
// Easy to unit test in isolation.
// ─────────────────────────────────────────────────────────────────────────────

import {createAsyncThunk} from '@reduxjs/toolkit'
import {accountsApi} from '../../../app-store/accounts/accountsApi'

// ─── Email lookup footer config ───────────────────────────────────────────────
// Declarative — maps outcome → buttons shown in footer.
// Each button has a label and a nav route.

export const EMAIL_FOOTER_CONFIG = {
  'new-customer': [
    {id: 'proceedToCreateAccount', label: 'Proceed to Create Account →'},
  ],
  'upgrade-customer': [{id: 'addLocation', label: 'View Results'}],
  'partner-owned-account': [{id: 'addLocation', label: 'View Results'}],
  'multiple-parent': [{id: 'addLocation', label: 'View Results'}],
}

// ─── Constants ────────────────────────────────────────────────────────────────

// Roles that allow the + character in email addresses
const PLUS_ALLOWED_ROLES = [
  'keappartner',
  'reseller',
  'admin',
  'KeapPartner',
  'Reseller',
  'Admin',
  'PartnerOps',
]

// Max accepted email length (RFC 5321 limit) — also a cheap DoS guard.
const MAX_EMAIL_LENGTH = 254

// Characters allowed in the local part for permissive (partner/admin) roles.
const PERMISSIVE_LOCAL_CHARS = new Set(
  'abcdefghijklmnopqrstuvwxyzABCDEFGHIJKLMNOPQRSTUVWXYZ0123456789._+-'.split(
    '',
  ),
)

const ERRORS = {
  INVALID_FORMAT: 'Valid email address required',
  THRYV_DOMAIN: 'Thryv addresses are not allowed',
}

// Single-character whitespace test without a regex (covers unicode spaces too).
const isWhitespace = ch => ch.trim() === ''

/**
 * Regex-free, linear-time email format check.
 *
 * Done with plain string scanning instead of a regex so there is no
 * backtracking engine involved at all — ReDoS-safe by construction.
 * Mirrors the previous regex behavior:
 *   - exactly one `@`, not in the first position
 *   - local part: no whitespace; standard roles also disallow `+`,
 *     permissive roles restrict to `[a-zA-Z0-9._+-]`
 *   - domain: dot-separated, non-empty labels, no whitespace, at least one dot
 *
 * @param {string}  email
 * @param {boolean} allowPlus - permissive (partner/admin) role
 * @returns {boolean}
 */
function isValidEmailFormat(email, allowPlus) {
  if (typeof email !== 'string') return false
  if (email.length === 0 || email.length > MAX_EMAIL_LENGTH) return false

  const at = email.indexOf('@')
  // Require exactly one '@', and it can't be the first character.
  if (at <= 0 || at !== email.lastIndexOf('@')) return false

  const local = email.slice(0, at)
  const domain = email.slice(at + 1)
  if (domain.length === 0) return false

  // ── Local part ──
  for (const ch of local) {
    if (isWhitespace(ch)) return false
    if (allowPlus) {
      if (!PERMISSIVE_LOCAL_CHARS.has(ch)) return false
    } else if (ch === '+') {
      return false
    }
  }

  // ── Domain: dot-separated, non-empty labels, no whitespace ──
  const labels = domain.split('.')
  if (labels.length < 2) return false // require at least one dot
  for (const label of labels) {
    if (label.length === 0) return false
    for (const ch of label) {
      if (isWhitespace(ch)) return false
    }
  }

  return true
}

// ─── Email format validation ──────────────────────────────────────────────────

/**
 * Validates email format based on the user's roles.
 * Partner/admin roles allow + in the local part; standard users do not.
 * Also blocks @thryv.com addresses.
 *
 * @param {string}   email
 * @param {string[]} roles  - user roles from auth context
 * @returns {string|null}   - error message or null if valid
 */
export function validateEmail(email, roles = []) {
  const hasPermissiveRole = roles.some(role =>
    PLUS_ALLOWED_ROLES.includes(role),
  )

  if (!isValidEmailFormat(email, hasPermissiveRole)) {
    return ERRORS.INVALID_FORMAT
  }
  if (email.includes('@thryv.com')) return ERRORS.THRYV_DOMAIN
  return null
}

// ─── API response interpreter ─────────────────────────────────────────────────

/**
 * Outcome values — what the API response means for the UI.
 *
 *   new-customer             — free to proceed, create new account
 *   existing-customer        — account exists, cannot continue normally
 *   upgrade-customer         — account exists but can be upgraded (v4 → v5)
 *   partner-misaligned       — email is valid but not aligned to this partner
 *   mix-subscription         — account has a mix of v4 and v5 subscriptions
 *   bni-restricted           — account has BNI addon, feature flag is off
 *   social-ads-bni-restricted— account has Social Ads BNI addon, feature flag is off
 *   multiple-parent          — account has multiple parent accounts
 *   invoice-billed           — account is invoice billed (special handling)
 */
export const OUTCOME = {
  NEW_CUSTOMER: 'new-customer',
  EXISTING_CUSTOMER: 'existing-customer',
  UPGRADE_CUSTOMER: 'upgrade-customer',
  PARTNER_MISALIGNED: 'partner-misaligned',
  PARTNER_OWNED_ACCOUNT: 'partner-owned-account',
  MIX_SUBSCRIPTION: 'mix-subscription',
  BNI_RESTRICTED: 'bni-restricted',
  SOCIAL_ADS_BNI_RESTRICTED: 'social-ads-bni-restricted',
  MULTIPLE_PARENT: 'multiple-parent',
  INVOICE_BILLED: 'invoice-billed',
}

/**
 * Interprets the validate-email API response into a single outcome string.
 * All branching logic lives here — the component just reacts to the outcome.
 *
 * Order of checks matches the original business priority exactly.
 *
 * @param {object} response           - raw API response
 * @param {object} featureFlags
 * @param {boolean} featureFlags.enableBniLeadsLift   - Enable_BNI_Leads_Lift flag
 * @param {boolean} featureFlags.enableBniSocialAds   - Enable_BNI_Social_Ads flag
 * @param {boolean} featureFlags.frontendInvoiceCc    - frontendInvoiceCc flag
 * @param {object} context
 * @param {boolean} context.isKeapPartnerFlow - whether lookup is running in Keap partner flow
 *
 * @returns {{ outcome: string, meta: object }}
 *   outcome — one of the OUTCOME values above
 *   meta    — relevant response fields the component may need (thryvId, etc.)
 */
export function interpretEmailResponse(
  response,
  featureFlags = {},
  context = {},
) {
  const {
    enableBniLeadsLift = false,
    enableBniSocialAds = false,
    frontendInvoiceCc = false,
  } = featureFlags
  const {isKeapPartnerFlow = false} = context

  // ── Partner misalignment — check first, highest priority ──────────────────
  if ('isValidPartnerAligned' in response && !response.isValidPartnerAligned) {
    return {outcome: OUTCOME.PARTNER_MISALIGNED, meta: {}}
  }

  // ── Mixed v4/v5 subscriptions ──────────────────────────────────────────────
  if ('mixOf4And5' in response && response.mixOf4And5) {
    return {outcome: OUTCOME.MIX_SUBSCRIPTION, meta: {}}
  }

  // ── BNI addon restricted ───────────────────────────────────────────────────
  if (response.hasBniAddon && !enableBniLeadsLift) {
    return {outcome: OUTCOME.BNI_RESTRICTED, meta: {}}
  }

  // ── Social Ads BNI addon restricted ───────────────────────────────────────
  if (response.hasSocialAdsBniAddon && !enableBniSocialAds) {
    return {outcome: OUTCOME.SOCIAL_ADS_BNI_RESTRICTED, meta: {}}
  }

  // ── Multiple parent accounts ───────────────────────────────────────────────
  if ('multipleParentAccount' in response && response.multipleParentAccount) {
    return {
      outcome: OUTCOME.MULTIPLE_PARENT,
      // The API includes this field only after verifying that each candidate
      // belongs to the requesting Keap partner. Account Setup passes it to the
      // embedded results view for its partner-scoped fallback lookup.
      meta: {partnerAccountCodes: response.partnerAccountCodes ?? []},
    }
  }

  if (
    response.isValidPartnerAligned !== true &&
    response.isInvoiceBilled &&
    frontendInvoiceCc
  ) {
    return {
      outcome: OUTCOME.INVOICE_BILLED,
      meta: {thryvId: response.thryvId},
    }
  }

  // ── Already registered — invoice-billed or has a paid subscription ─────────
  // Block invoice-billed accounts globally. The paid-account guard
  // (`freeAccount === false`) is only a Keap partner rule; core OOE should still
  // honor `accountCanUpgrade` and continue to the upgrade / "View Results" path.
  //
  // Exception: accounts that belong to THIS keap partner (`isValidPartnerAligned
  // === true`) are NOT blocked — the partner may manage their own existing
  // customers, so they fall through to the upgrade / "View Results" path.
  // (A different partner's account is already returned as PARTNER_MISALIGNED
  // above; absent alignment info means no partner context, so we still block.)
  if (
    response.isValidPartnerAligned !== true &&
    (response.isInvoiceBilled ||
      (isKeapPartnerFlow && response.freeAccount === false))
  ) {
    return {outcome: OUTCOME.EXISTING_CUSTOMER, meta: {}}
  }

  // ── New customer — free to proceed ─────────────────────────────────────────
  if (response.orderCanContinue) {
    return {outcome: OUTCOME.NEW_CUSTOMER, meta: {}}
  }

  // ── Upgrade path ───────────────────────────────────────────────────────────
  if (!response.orderCanContinue && response.accountCanUpgrade) {
    return {
      outcome: OUTCOME.UPGRADE_CUSTOMER,
      meta: response,
      //   meta: {
      //     thryvId: response.thryvId,
      //     subscriptionId: response.subscriptionId,
      //     isV5Upgrade: response?.v5upgrade ?? false,
      //   },
    }
  }

  // ── Existing account — cannot continue ─────────────────────────────────────
  if (!response.orderCanContinue && !response.accountCanUpgrade) {
    // Account belongs to this partner — let them view it instead of blocking
    if (response.isValidPartnerAligned) {
      return {outcome: OUTCOME.PARTNER_OWNED_ACCOUNT, meta: response}
    }
    return {outcome: OUTCOME.EXISTING_CUSTOMER, meta: {}}
  }

  // ── Fallback ───────────────────────────────────────────────────────────────
  return {outcome: OUTCOME.EXISTING_CUSTOMER, meta: {}}
}

// ─── Error message interpreter ────────────────────────────────────────────────

/**
 * Maps a caught API error to a user-facing message.
 * Keeps error copy out of the component.
 *
 * @param {Error}   err
 * @param {boolean} frontendUserError - feature flag for user-facing error copy
 * @returns {string}
 */
export function getApiErrorMessage(err, frontendUserError = false) {
  if (err?.message === 'Recurly account not found') {
    return frontendUserError
      ? 'Looks like this email exists in one of our systems. Please contact support to have this resolved.'
      : 'Account not found in billing system. Please contact support.'
  }
  return err?.message ?? 'Something went wrong. Please try again.'
}

// ─── Main API call ────────────────────────────────────────────────────────────

/**
 * Calls the validate-email endpoint and returns an interpreted outcome.
 * Throws on network/API error — caller handles via try/catch.
 *
 * @param {object}   params
 * @param {string}   params.email
 * @param {Function} params.run          - API runner (from useAsync hook)
 * @param {Function} params.client       - authenticated API client
 * @param {object}   params.featureFlags - { enableBniLeadsLift, enableBniSocialAds, frontendInvoiceCc }
 *
 * @returns {Promise<{ outcome: string, meta: object }>}
 */
export async function validateEmailWithApi({
  email,
  run,
  client,
  featureFlags,
  keapPartnerId,
  flowType = '',
}) {
  const queryParams = keapPartnerId
    ? `?fetchSubDetail=true&keapPartnerId=${keapPartnerId}&keapClient=true`
    : `?fetchSubDetail=true`
  const response =
    flowType === 'Proposal'
      ? await client(`orders/validate-email/${email}${queryParams}`)
      : await run(client(`orders/validate-email/${email}${queryParams}`))
  return interpretEmailResponse(response, featureFlags, {
    isKeapPartnerFlow: Boolean(keapPartnerId),
  })
}

export const fetchAccounts = createAsyncThunk(
  'accounts/fetchAccounts',
  async ({client, email, thryvId}, {rejectWithValue}) => {
    try {
      return await accountsApi.getAccounts(client, {email, thryvId})
    } catch (err) {
      return rejectWithValue(err?.response?.data ?? 'Failed to fetch accounts')
    }
  },
)
